/**
 * Painting to Gogh — announcement bar JS.
 *
 * Two custom elements:
 *  <cd-countdown data-end="ISO" data-expired-action="..." data-expired-text="...">
 *    Hydrates a server-rendered placeholder into a live D/H/M/S counter.
 *    On expire: either replaces text with `data-expired-text`, or hides
 *    the parent .cd-announcement-bar__message entirely.
 *
 *  <cd-announcement-bar data-interval="5000">
 *    If 2+ .cd-announcement-bar__message children, rotates the
 *    .is-active class on a fixed interval. Single-message bars are
 *    static (no JS work).
 */

class CdCountdown extends HTMLElement {
  connectedCallback() {
    const end = this.dataset.end;
    if (!end) return;
    this.endDate = new Date(end);
    if (Number.isNaN(this.endDate.getTime())) {
      this.textContent = '';
      return;
    }
    this.expiredAction = this.dataset.expiredAction || 'show_text';
    this.expiredText = this.dataset.expiredText || '';
    this.update();
    this.timer = setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    if (this.timer) clearInterval(this.timer);
  }

  update() {
    const diff = this.endDate.getTime() - Date.now();
    if (diff <= 0) {
      clearInterval(this.timer);
      this.timer = null;
      if (this.expiredAction === 'hide_message') {
        const msg = this.closest('.cd-announcement-bar__message');
        if (msg) msg.dataset.cdExpired = 'true';
      } else {
        this.replaceWithExpired();
      }
      return;
    }
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    this.setUnit('days', days);
    this.setUnit('hours', hours);
    this.setUnit('minutes', mins);
    this.setUnit('seconds', secs);
  }

  setUnit(unit, value) {
    const el = this.querySelector(`[data-unit="${unit}"]`);
    if (el) el.textContent = String(value).padStart(2, '0');
  }

  replaceWithExpired() {
    while (this.firstChild) this.removeChild(this.firstChild);
    const span = document.createElement('span');
    span.className = 'cd-announcement-bar__timer-expired';
    span.textContent = this.expiredText;
    this.appendChild(span);
  }
}
if (!customElements.get('cd-countdown')) {
  customElements.define('cd-countdown', CdCountdown);
}

class CdAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.messages = Array.from(this.querySelectorAll('.cd-announcement-bar__message'));
    if (this.messages.length <= 1) return;
    this.idx = 0;
    const interval = parseInt(this.dataset.interval || '5000', 10);
    this.timer = setInterval(() => this.advance(), interval);
  }

  disconnectedCallback() {
    if (this.timer) clearInterval(this.timer);
  }

  advance() {
    const live = this.messages.filter((m) => m.dataset.cdExpired !== 'true');
    if (live.length <= 1) {
      clearInterval(this.timer);
      return;
    }
    const currentIdx = live.findIndex((m) => m.classList.contains('is-active'));
    if (currentIdx >= 0) live[currentIdx].classList.remove('is-active');
    const nextIdx = (currentIdx + 1) % live.length;
    live[nextIdx].classList.add('is-active');
  }
}
if (!customElements.get('cd-announcement-bar')) {
  customElements.define('cd-announcement-bar', CdAnnouncementBar);
}
